import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronDown, X, Filter, SlidersHorizontal, ArrowUpDown } from 'lucide-react'
import ProductCard from '../ProductCard'
import { db } from '../../utils/firebase'
import { collection, getDocs, doc, getDoc, query, orderBy } from 'firebase/firestore'

const PAGE_SIZE = 25;

// ── Skeleton Card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
    return (
        <div className="rounded-[28px] border border-[#D7DDD4] bg-white p-3.5 flex flex-col gap-3 animate-pulse">
            <div className="w-full aspect-square rounded-[20px] bg-[#EAEDE8]" />
            <div className="space-y-2">
                <div className="h-4 bg-[#EAEDE8] rounded-full w-4/5" />
                <div className="h-3 bg-[#EAEDE8] rounded-full w-2/5" />
                <div className="h-3 bg-[#EAEDE8] rounded-full w-1/2" />
            </div>
            <div className="flex gap-2 mt-1">
                <div className="h-9 bg-[#EAEDE8] rounded-xl flex-1" />
                <div className="h-9 bg-[#EAEDE8] rounded-xl w-16" />
            </div>
        </div>
    );
}

// ── Sort options ──────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
    { value: 'default', label: 'Default' },
    { value: 'name_asc', label: 'Name A → Z' },
    { value: 'name_desc', label: 'Name Z → A' },
];

export default function Products({
    searchQuery, setSearchQuery,
    selectedCategory, setSelectedCategory,
    selectedSubcategory, setSelectedSubcategory,
    selectedSectionId, setSelectedSectionId,
    selectedSectionName, setSelectedSectionName,
    handleAddToCart, myList, handleToggleMyList,
    cartCount, onCartClick, onStoreLocationsClick, onAuthClick
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    // ── Sync URL ↔ state ──────────────────────────────────────────────────────
    useEffect(() => {
        const queryVal = searchParams.get('q') || '';
        const catVal = searchParams.get('category') || null;
        const subVal = searchParams.get('subcategory') || null;
        const secIdVal = searchParams.get('sectionId') || null;
        const secNameVal = searchParams.get('sectionName') || '';
        if (queryVal !== searchQuery) setSearchQuery(queryVal);
        if (catVal !== selectedCategory) setSelectedCategory(catVal);
        if (subVal !== selectedSubcategory) setSelectedSubcategory(subVal);
        if (secIdVal !== selectedSectionId) setSelectedSectionId(secIdVal);
        if (secNameVal !== selectedSectionName) setSelectedSectionName(secNameVal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        const params = {};
        if (searchQuery) params.q = searchQuery;
        if (selectedCategory) params.category = selectedCategory;
        if (selectedSubcategory) params.subcategory = selectedSubcategory;
        if (selectedSectionId) {
            params.sectionId = selectedSectionId;
            if (selectedSectionName) params.sectionName = selectedSectionName;
        }
        setSearchParams(params, { replace: true });
    }, [searchQuery, selectedCategory, selectedSubcategory, selectedSectionId, selectedSectionName, setSearchParams]);

    // ── Filter states ─────────────────────────────────────────────────────────
    const [showMyListOnly, setShowMyListOnly] = useState(false);
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [sortBy, setSortBy] = useState('default');
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const sortRef = useRef(null);

    // ── Products loading states ───────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    // ── Facets ────────────────────────────────────────────────────────────────
    const [facets, setFacets] = useState({ type: {}, category: {}, subcategory: {}, brand: {} });
    const [sectionCategories, setSectionCategories] = useState(null);

    // ── Filter lists (persisted so checkboxes don't vanish after filtering) ───
    const [typesList, setTypesList] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);
    const [subcategoriesList, setSubcategoriesList] = useState([]);
    const [persistedTypes, setPersistedTypes] = useState([]);
    const [persistedCategories, setPersistedCategories] = useState([]);
    const [persistedSubcategories, setPersistedSubcategories] = useState([]);
    const [persistedBrands, setPersistedBrands] = useState([]);

    // ── Close sort dropdown on outside click ──────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target))
                setShowSortDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Fetch filter lists from Firestore ─────────────────────────────────────
    useEffect(() => {
        const fetchFilterLists = async () => {
            try {
                const [typesSnap, catsSnap, subsSnap] = await Promise.all([
                    getDocs(query(collection(db, 'types'), orderBy('order'))),
                    getDocs(query(collection(db, 'categories'), orderBy('order'))),
                    getDocs(query(collection(db, 'sub_categories'), orderBy('order'))),
                ]);
                const tList = typesSnap.docs.filter(d => d.data().status !== false).map(d => d.data().title || d.data().name || d.id);
                const cList = catsSnap.docs.filter(d => d.data().status !== false).map(d => d.data().title || d.data().name || d.id);
                const sList = subsSnap.docs.filter(d => d.data().status !== false).map(d => d.data().title || d.data().name || d.id);
                setTypesList(tList);
                setCategoriesList(cList);
                setSubcategoriesList(sList);
                setPersistedTypes(prev => Array.from(new Set([...prev, ...tList])));
                setPersistedCategories(prev => Array.from(new Set([...prev, ...cList])));
                setPersistedSubcategories(prev => Array.from(new Set([...prev, ...sList])));
            } catch (e) {
                console.warn('Could not fetch filter lists:', e);
            }
        };
        fetchFilterLists();
    }, []);

    // ── Fetch section details ─────────────────────────────────────────────────
    useEffect(() => {
        if (!selectedSectionId) { setSectionCategories(null); return; }
        const fetchSectionDetails = async () => {
            try {
                const snap = await getDoc(doc(db, 'sections', selectedSectionId));
                setSectionCategories(snap.exists() ? (snap.data().categories || []) : null);
            } catch (e) {
                console.error('Error fetching section:', e);
                setSectionCategories(null);
            }
        };
        fetchSectionDetails();
    }, [selectedSectionId]);


    // ── Raw products state ───────────────────────────────────────────────────
    const [allProducts, setAllProducts] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    // ── Fetch all products from Firestore ────────────────────────────────────
    useEffect(() => {
        let isMounted = true;
        const fetchAllProducts = async () => {
            setIsLoading(true);
            try {
                const snap = await getDocs(collection(db, 'products'));
                const fetched = snap.docs.map(doc => {
                    const data = doc.data();
                    const docId = doc.id || '';
                    const displayCode = data.productCodeLetters && data.productCode
                        ? `${data.productCodeLetters}${data.productCode}`
                        : (data.code || '');
                    return {
                        id: docId,
                        title: data.productName || data.title || '',
                        code: displayCode,
                        image: data.image || (data.images?.[0]) || '/product-placeholder.png',
                        brand: data.brandName || data.brand || '',
                        type: data.type_name || data.type || '',
                        category: data.categoryName || data.category || '',
                        subcategory: data.subCategoryName || data.subcategory || '',
                        order: data.order !== undefined ? data.order : 9999,
                        ...data,
                    };
                }).filter(p => p.status !== false);

                if (isMounted) {
                    setAllProducts(fetched);
                }
            } catch (e) {
                console.error('Error fetching all products from Firestore:', e);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        fetchAllProducts();
        return () => { isMounted = false; };
    }, []);

    // ── Compute filtered + sorted products ────────────────────────────────────
    const filteredProducts = React.useMemo(() => {
        let list = [...allProducts];

        // 1. Search Query Substring Match
        if (searchQuery && searchQuery.trim() !== '') {
            const q = searchQuery.trim().toLowerCase();
            list = list.filter(p => {
                const nameMatch = (p.title || '').toLowerCase().includes(q) || (p.productName || '').toLowerCase().includes(q);
                const codeMatch = (p.code || '').toLowerCase().includes(q);
                const brandMatch = (p.brand || '').toLowerCase().includes(q) || (p.brandName || '').toLowerCase().includes(q);
                const catMatch = (p.category || '').toLowerCase().includes(q) || (p.categoryName || '').toLowerCase().includes(q);
                const subMatch = (p.subcategory || '').toLowerCase().includes(q) || (p.subCategoryName || '').toLowerCase().includes(q);
                const typeMatch = (p.type || '').toLowerCase().includes(q) || (p.type_name || '').toLowerCase().includes(q);
                return nameMatch || codeMatch || brandMatch || catMatch || subMatch || typeMatch;
            });
        }

        // 2. Section / Category
        if (selectedSectionId) {
            list = list.filter(p => p.sectionIds && Array.isArray(p.sectionIds) && p.sectionIds.includes(selectedSectionId));
        } else if (selectedCategory) {
            list = list.filter(p => (p.category || p.categoryName) === selectedCategory || (p.type || p.type_name) === selectedCategory);
        } else if (selectedCategories.length > 0) {
            list = list.filter(p => selectedCategories.includes(p.category) || selectedCategories.includes(p.categoryName) || selectedCategories.includes(p.type) || selectedCategories.includes(p.type_name));
        }

        // 3. Subcategory
        if (selectedSubcategory) {
            list = list.filter(p => (p.subcategory || p.subCategoryName) === selectedSubcategory);
        } else if (selectedSubcategories.length > 0) {
            list = list.filter(p => selectedSubcategories.includes(p.subcategory) || selectedSubcategories.includes(p.subCategoryName));
        }

        // 4. Brand
        if (selectedBrands.length > 0) {
            list = list.filter(p => selectedBrands.includes(p.brand) || selectedBrands.includes(p.brandName));
        }

        // 5. Type
        if (selectedTypes.length > 0) {
            list = list.filter(p => selectedTypes.includes(p.type) || selectedTypes.includes(p.type_name));
        }

        // 6. My List Only
        if (showMyListOnly) {
            list = list.filter(p => myList.includes(p.id) || (p.code && myList.includes(p.code)) || (p.title && myList.includes(p.title)));
        }

        // 7. Sort
        if (sortBy === 'name_asc') {
            list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        } else if (sortBy === 'name_desc') {
            list.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
        } else {
            list.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        }

        return list;
    }, [
        allProducts, searchQuery, selectedSectionId, selectedCategory, selectedCategories,
        selectedSubcategory, selectedSubcategories, selectedBrands, selectedTypes,
        showMyListOnly, myList, sortBy
    ]);

    // ── Update facets whenever allProducts change ─────────────────────────────
    useEffect(() => {
        const typeFacets = {}, catFacets = {}, subFacets = {}, brandFacets = {};
        allProducts.forEach(item => {
            if (item.type) typeFacets[item.type] = (typeFacets[item.type] || 0) + 1;
            if (item.category) catFacets[item.category] = (catFacets[item.category] || 0) + 1;
            if (item.subcategory) subFacets[item.subcategory] = (subFacets[item.subcategory] || 0) + 1;
            if (item.brand) brandFacets[item.brand] = (brandFacets[item.brand] || 0) + 1;
        });
        setFacets({
            type: typeFacets,
            category: catFacets,
            subcategory: subFacets,
            brand: brandFacets,
        });
        setPersistedTypes(prev => Array.from(new Set([...prev, ...Object.keys(typeFacets), ...typesList])));
        setPersistedCategories(prev => Array.from(new Set([...prev, ...Object.keys(catFacets), ...categoriesList])));
        setPersistedSubcategories(prev => Array.from(new Set([...prev, ...Object.keys(subFacets), ...subcategoriesList])));
        setPersistedBrands(prev => Array.from(new Set([...prev, ...Object.keys(brandFacets)])));
    }, [allProducts, typesList, categoriesList, subcategoriesList]);

    // ── Reset visible limit on filter/search change ───────────────────────────
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [
        searchQuery, selectedCategory, selectedSubcategory, selectedSectionId,
        selectedBrands, selectedCategories, selectedTypes, selectedSubcategories,
        sortBy, showMyListOnly
    ]);

    // ── Displayed slice + pagination tracking ─────────────────────────────────
    const productsList = filteredProducts.slice(0, visibleCount);
    const hasMore = visibleCount < filteredProducts.length;

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleLoadMore = () => {
        if (!hasMore || isLoading) return;
        setLoadingMore(true);
        setTimeout(() => {
            setVisibleCount(prev => prev + PAGE_SIZE);
            setLoadingMore(false);
        }, 200);
    };

    const clearFilters = () => {
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedSectionId(null);
        setSelectedSectionName('');
        setSearchQuery('');
        setSelectedBrands([]);
        setSelectedCategories([]);
        setSelectedTypes([]);
        setSelectedSubcategories([]);
        setSortBy('default');
        setIsMobileFilterOpen(false);
        setShowMyListOnly(false);
        setVisibleCount(PAGE_SIZE);
    };

    const handleBrandToggle = b => setSelectedBrands(p => p.includes(b) ? p.filter(x => x !== b) : [...p, b]);
    const handleCategoryToggle = c => setSelectedCategories(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);
    const handleTypeToggle = t => setSelectedTypes(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);
    const handleSubcategoryToggle = s => setSelectedSubcategories(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);

    // ── Active filter chips ───────────────────────────────────────────────────
    const activeChips = [
        ...(selectedSectionName ? [{ label: `Section: ${selectedSectionName}`, onRemove: () => { setSelectedSectionId(null); setSelectedSectionName(''); } }] : []),
        ...(selectedCategory && !selectedCategories.length ? [{ label: selectedCategory, onRemove: () => setSelectedCategory(null) }] : []),
        ...(selectedSubcategory && !selectedSubcategories.length ? [{ label: selectedSubcategory, onRemove: () => setSelectedSubcategory(null) }] : []),
        ...selectedCategories.map(c => ({ label: c, onRemove: () => handleCategoryToggle(c) })),
        ...selectedSubcategories.map(s => ({ label: s, onRemove: () => handleSubcategoryToggle(s) })),
        ...selectedBrands.map(b => ({ label: b, onRemove: () => handleBrandToggle(b) })),
        ...selectedTypes.map(t => ({ label: t, onRemove: () => handleTypeToggle(t) })),
        ...(showMyListOnly ? [{ label: 'My List Only', onRemove: () => setShowMyListOnly(false) }] : []),
    ];

    const hasAnyFilter = activeChips.length > 0 || (searchQuery && searchQuery.trim() !== '');

    // ── Sidebar ───────────────────────────────────────────────────────────────
    const renderSidebarContent = () => (
        <div className="products-sidebar-container sidebar-scrollbar-hidden">
            <div className="products-sidebar-header">
                <div className="products-sidebar-icon">
                    <Filter className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="products-sidebar-title">Filters</h3>
                    <p className="products-sidebar-subtitle">Refine your search</p>
                </div>
            </div>

            <div className="products-sidebar-subheader">
                <span className="font-bold text-slate-800 text-sm">Filter</span>
                {hasAnyFilter && (
                    <button onClick={clearFilters} className="products-sidebar-clear hover:shadow-md hover:-translate-y-0.5 transition-all">
                        Clear All
                    </button>
                )}
            </div>

            {/* My List toggle */}
            <div className="mb-4 pb-4 border-b border-[#E8EBE6]">
                <label className="flex items-center gap-3 cursor-pointer">
                    <div
                        onClick={() => setShowMyListOnly(p => !p)}
                        className={`relative w-10 rounded-full transition-colors duration-300 cursor-pointer flex-shrink-0 ${showMyListOnly ? 'bg-[#2C3E2B]' : 'bg-[#D7DDD4]'}`}
                        style={{ height: '22px' }}
                    >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${showMyListOnly ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-sm font-semibold text-[#2C3E2B]">My List Only</span>
                </label>
            </div>

            <div className="space-y-5">
                {/* Types */}
                {persistedTypes.length > 0 && (
                    <div className="products-filter-group">
                        <h4 className="products-filter-title">Types</h4>
                        <div className="products-filter-box">
                            {persistedTypes.map((typeName, i) => (
                                <label key={i} className="products-filter-label">
                                    <div className="flex items-center gap-2.5">
                                        <input type="checkbox" checked={selectedTypes.includes(typeName)} onChange={() => handleTypeToggle(typeName)} className="products-filter-checkbox" />
                                        <span>{typeName}</span>
                                    </div>
                                    <span className="products-filter-count">({facets.type[typeName] || 0})</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Categories */}
                {!selectedSectionId && persistedCategories.length > 0 && (
                    <div className="products-filter-group">
                        <h4 className="products-filter-title">Categories</h4>
                        <div className="products-filter-box">
                            {persistedCategories.map((catName, i) => (
                                <label key={i} className="products-filter-label">
                                    <div className="flex items-center gap-2.5">
                                        <input type="checkbox" checked={selectedCategories.includes(catName)} onChange={() => handleCategoryToggle(catName)} className="products-filter-checkbox" />
                                        <span>{catName}</span>
                                    </div>
                                    <span className="products-filter-count">({facets.category[catName] || 0})</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Sub Categories */}
                {(Object.keys(facets.subcategory || {}).length > 0 || subcategoriesList.length > 0) && (
                    <div className="products-filter-group">
                        <h4 className="products-filter-title">Sub Categories</h4>
                        <div className="products-filter-box">
                            {persistedSubcategories.map((subName, i) => (
                                <label key={i} className="products-filter-label">
                                    <div className="flex items-center gap-2.5">
                                        <input type="checkbox" checked={selectedSubcategories.includes(subName)} onChange={() => handleSubcategoryToggle(subName)} className="products-filter-checkbox" />
                                        <span>{subName}</span>
                                    </div>
                                    <span className="products-filter-count">({facets.subcategory[subName] || 0})</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Brands */}
                {persistedBrands.length > 0 && (
                    <div className="products-filter-group">
                        <h4 className="products-filter-title">Brands</h4>
                        <div className="products-filter-box">
                            {persistedBrands.map((brand, i) => (
                                <label key={i} className="products-filter-label">
                                    <div className="flex items-center gap-2.5">
                                        <input type="checkbox" checked={selectedBrands.includes(brand)} onChange={() => handleBrandToggle(brand)} className="products-filter-checkbox" />
                                        <span>{brand}</span>
                                    </div>
                                    <span className="products-filter-count">({facets.brand[brand] || 0})</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const pageTitle = searchQuery
        ? `Results for "${searchQuery}"`
        : selectedSubcategory
            ? selectedSubcategory
            : selectedCategory
                ? selectedCategory
                : selectedSectionId
                    ? selectedSectionName
                    : 'All Products';

    return (
        <div className="products-page-container">


            {/* ── Main Content ───────────────────────────────────────────────── */}
            <div className="products-main-content">
                {/* Mobile header */}
                <div className="products-mobile-header flex items-center justify-between">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <h2 className="products-section-title text-left m-0" style={{ marginBottom: 0 }}>{pageTitle}</h2>
                        {!isLoading && productsList.length > 0 && (
                            <span className="text-xs text-[#8BA888] font-semibold whitespace-nowrap">
                                {productsList.length} product{productsList.length !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <button onClick={() => setIsMobileFilterOpen(true)} className="products-mobile-filter-btn shrink-0">
                        <Filter className="w-3.5 h-3.5" />
                        Filters
                    </button>
                </div>

                <div className="products-layout-wrapper">
                    {/* Desktop Sidebar */}
                    <div className="products-sidebar-desktop">
                        {renderSidebarContent()}
                    </div>

                    {/* Products Column */}
                    <div className="products-main-col">
                        {/* Desktop title & Sort Toolbar */}
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-4 products-desktop-header-row hidden md:flex">
                            <div className="flex items-baseline gap-3">
                                <h2 className="products-section-title products-desktop-title m-0" style={{ marginBottom: 0 }}>{pageTitle}</h2>
                                {!isLoading && productsList.length > 0 && (
                                    <span className="text-sm text-[#8BA888] font-semibold whitespace-nowrap">
                                        {productsList.length} product{productsList.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-3 ml-auto flex-shrink-0">
                                {/* Sort dropdown */}
                                <div className="relative" ref={sortRef}>
                                    <button
                                        onClick={() => setShowSortDropdown(p => !p)}
                                        className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-[#D7DDD4] bg-white text-xs font-semibold text-[#243126] hover:border-[#2C3E2B] transition-all whitespace-nowrap"
                                    >
                                        <ArrowUpDown className="w-3.5 h-3.5 text-[#8BA888]" />
                                        {SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Sort'}
                                        <ChevronDown className={`w-3 h-3 text-[#8BA888] transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                                    </button>
                                    {showSortDropdown && (
                                        <div className="absolute right-0 top-full mt-1.5 bg-white border border-[#D7DDD4] rounded-xl shadow-xl z-50 min-w-40 overflow-hidden">
                                            {SORT_OPTIONS.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => { setSortBy(opt.value); setShowSortDropdown(false); }}
                                                    className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-colors ${sortBy === opt.value ? 'bg-[#2C3E2B] text-white font-bold' : 'text-[#243126] hover:bg-[#F1F3F0]'}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Active filter chips */}
                        {activeChips.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                                {activeChips.map((chip, i) => (
                                    <span key={i} className="flex items-center gap-1.5 px-3 py-1 bg-[#2C3E2B] text-white text-xs font-semibold rounded-full">
                                        {chip.label}
                                        <button onClick={chip.onRemove} className="hover:text-white/60 transition-colors" aria-label={`Remove ${chip.label}`}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                                <button onClick={clearFilters} className="px-3 py-1 border border-[#D7DDD4] text-[#5D6962] text-xs font-semibold rounded-full hover:border-[#2C3E2B] hover:text-[#2C3E2B] transition-all">
                                    Clear All
                                </button>
                            </div>
                        )}

                        {/* Section category tabs */}
                        {selectedSectionId && sectionCategories && sectionCategories.length > 0 && (
                            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-none flex-nowrap touch-pan-x" style={{ WebkitOverflowScrolling: 'touch' }}>
                                <button
                                    onClick={() => setSelectedCategories([])}
                                    className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all border-none cursor-pointer flex-shrink-0 ${selectedCategories.length === 0 ? 'bg-[#2C3E2B] text-white shadow-md' : 'bg-[#F1F3F0] text-[#2C3E2B] hover:bg-[#dedede]'}`}
                                >All</button>
                                {sectionCategories.map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategories([cat.title])}
                                        className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all border-none cursor-pointer flex-shrink-0 ${selectedCategories.includes(cat.title) ? 'bg-[#2C3E2B] text-white shadow-md' : 'bg-[#F1F3F0] text-[#2C3E2B] hover:bg-[#dedede]'}`}
                                    >{cat.title}</button>
                                ))}
                            </div>
                        )}

                        {/* ── Products grid ────────────────────────────────────── */}
                        {isLoading ? (
                            <div className="products-grid mt-0">
                                {Array.from({ length: PAGE_SIZE }).map((_, i) => <SkeletonCard key={i} />)}
                            </div>
                        ) : productsList.length > 0 ? (
                            <>
                                <div className="products-grid mt-0">
                                    {productsList.map((product, idx) => (
                                        <ProductCard
                                            key={product.id || idx}
                                            product={product}
                                            onAddToCart={handleAddToCart}
                                            isLiked={myList.includes(product.id) || (product.code && myList.includes(product.code)) || (product.title && myList.includes(product.title))}
                                            onToggleMyList={() => handleToggleMyList(product.id || product.code || product.title)}
                                        />
                                    ))}
                                </div>

                                {/* Load More */}
                                <div className="flex flex-col items-center mt-10 mb-4 gap-2">
                                    {loadingMore ? (
                                        <div className="flex items-center gap-2 text-sm text-[#8BA888] font-semibold">
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#2C3E2B]" />
                                            Loading more…
                                        </div>
                                    ) : hasMore ? (
                                        <button
                                            onClick={handleLoadMore}
                                            className="px-10 py-3 rounded-xl bg-[#2C3E2B] text-white font-bold text-sm shadow-md hover:bg-[#1e2b1d] hover:shadow-lg hover:-translate-y-0.5 transition-all border-none cursor-pointer flex items-center gap-2"
                                        >
                                            <SlidersHorizontal className="w-4 h-4" />
                                            Load More Products
                                        </button>
                                    ) : (
                                        <p className="text-xs text-[#A8B4A5] font-semibold">
                                            All {productsList.length} products shown
                                        </p>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="products-empty-state">
                                <div className="text-slate-400 text-5xl mb-4">
                                    {searchQuery ? '🔍' : '📦'}
                                </div>
                                <h3 className="text-lg font-bold text-[#2C3E2B]">
                                    {searchQuery ? `No results for "${searchQuery}"` : 'No products found'}
                                </h3>
                                <p className="text-sm text-slate-500 mt-1 max-w-xs text-center">
                                    {searchQuery
                                        ? 'Try a different keyword, or clear your filters and browse all products.'
                                        : "We don't have any products here right now. Try checking other categories!"
                                    }
                                </p>
                                <button onClick={clearFilters} className="products-empty-btn hover:shadow-md hover:-translate-y-0.5 transition-all mt-4">
                                    View All Products
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Mobile Filter Drawer ─────────────────────────────────────────── */}
            {isMobileFilterOpen && (
                <div className="products-mobile-drawer-overlay">
                    <div className="products-mobile-drawer-backdrop" onClick={() => setIsMobileFilterOpen(false)} />
                    <div className="products-mobile-drawer-content">
                        <div className="products-mobile-drawer-header">
                            <span className="products-mobile-drawer-title">Filters</span>
                            <button onClick={() => setIsMobileFilterOpen(false)} className="products-mobile-drawer-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="products-mobile-drawer-body">
                            {renderSidebarContent()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
